import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import { 
  Heart, 
  HelpCircle, 
  GraduationCap, 
  Tag, 
  ArrowRight, 
  Check, 
  Sparkles, 
  Calendar, 
  Clock, 
  ShieldCheck,
  Smile,
  Users,
  MessageSquare,
  Quote
} from 'lucide-react';
import { SEO } from '../components/SEO';

export default function Theraplay() {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollection('theraplay_content').then(data => {
      // Map data by section_key for easy access
      const mapped = data.reduce((acc, item) => {
        acc[item.section_key] = item;
        return acc;
      }, {});
      setContent(mapped);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f2ec]/30">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4e6354]"></div>
    </div>
  );

  const aboutData = content['about_theraplay'];
  const howItWorksData = content['how_it_works'];
  const backgroundData = content['my_background'];
  const priceData = content['package_price'];

  return (
    <div className="bg-white overflow-hidden">
      <SEO 
        title="Theraplay | Hjertevarm familieterapi til børn 0-12 år"
        description="Styrk båndet og tilliden med Theraplay. En hjertevarm familieterapi til børn (0-12 år) med tilknytningsforstyrrelser, traumer, ADHD eller autisme. Læs her."
        path="/theraplay"
      />
      {/* Hero Section */}
      <section className="relative h-[55vh] md:h-[65vh] min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/shutterstock-2737390615_2.jpg" 
          alt="Theraplay samspil og tilknytning" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/50 via-stone-900/40 to-stone-900/60" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 md:pt-32">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-light text-white uppercase tracking-tight mt-2"
          >
            Theraplay
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-stone-200 text-lg md:text-xl max-w-2xl mx-auto mt-4 font-light leading-relaxed"
          >
            En hjertevarm og aktiv familieterapi, der styrker båndet og tilliden mellem dig og dit barn gennem leg.
          </motion.p>
        </div>

        {/* Wave Divider at bottom of Hero */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="relative block w-full h-12 md:h-24 fill-white"
          >
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </section>

      {/* Section 1: Hvad er Theraplay & Forståelse */}
      {aboutData && (
        <section className="py-20 md:py-28 bg-white relative overflow-hidden">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Left text */}
              <div className="lg:col-span-7">
                <span className="text-xs font-semibold tracking-widest text-[#4e6354] uppercase block mb-2">
                  Styrk relationen
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-stone-900 uppercase tracking-tight mb-6 mt-2">
                  {aboutData.title.da}
                </h2>
                <div className="w-12 h-1 bg-[#4e6354] mb-8"></div>
                
                <div 
                  className="prose prose-stone text-stone-600 max-w-none prose-p:leading-relaxed prose-p:text-lg prose-p:font-light space-y-6"
                  dangerouslySetInnerHTML={{ __html: aboutData.content.da }}
                />

                {/* Quote block under the text */}
                <motion.div 
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="mt-10 p-6 md:p-8 bg-stone-50 border-l-4 border-[#4e6354] relative"
                >
                  <Quote className="w-8 h-8 text-[#4e6354]/10 absolute top-4 right-4" />
                  <p className="text-base md:text-lg italic text-stone-700 font-light leading-relaxed">
                    "Leg er den korteste vej mellem to mennesker. Gennem Theraplay genfinder vi glæden ved at være sammen."
                  </p>
                  <div className="w-8 h-px bg-[#4e6354]/20 my-4"></div>
                  <p className="text-xs text-stone-500 uppercase tracking-widest font-medium">Anette Lundager Schädler</p>
                </motion.div>
              </div>

              {/* Right image */}
              <div className="lg:col-span-5 relative mt-12 lg:mt-0">
                <div className="aspect-[4/5] rounded-3xl rounded-tr-[100px] rounded-bl-[100px] overflow-hidden shadow-lg border border-stone-200/40 relative z-0">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/shutterstock-2250019467_1.jpg" 
                    alt="Theraplay leg og samspil" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Watermark Overlay - Winding Road */}
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.08] flex items-start justify-center">
            <img 
              src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/95574613-b9ad-4e75-84fb-52de2d444fa1.png" 
              alt="" 
              className="w-full h-full object-cover scale-150 rotate-6 mix-blend-multiply object-top"
            />
          </div>
        </section>
      )}



      {/* Section 2: Hvordan foregår det & De 4 Dimensioner */}
      {howItWorksData && (
        <section className="pt-28 md:pt-36 pb-20 md:pb-24 bg-gradient-to-b from-stone-50 via-stone-50/50 to-transparent relative overflow-hidden">
          {/* Wave Divider at top of Section 2 */}
          <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-20 transform rotate-180">
            <svg 
              viewBox="0 0 1200 120" 
              preserveAspectRatio="none" 
              className="relative block w-full h-12 md:h-24 fill-white"
            >
              <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
            </svg>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Header */}
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-xs font-semibold tracking-widest text-[#4e6354] uppercase block mb-2">
                Metoden & Processen
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-stone-900 uppercase tracking-tight mt-4 mb-6">
                {howItWorksData.title.da}
              </h2>
              <p className="text-stone-600 font-light text-lg leading-relaxed">
                Theraplay er bygget op omkring fire grundlæggende dimensioner, som vi bringer i spil for at skabe balance, glæde og tryghed i relationen.
              </p>
            </div>

            {/* The 4 Dimensions Badges */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-24">
              {[
                { name: 'Struktur', desc: 'Skaber tryghed og forudsigelighed' },
                { name: 'Engagement', desc: 'Skaber glæde og intenst nærvær' },
                { name: 'Omsorg', desc: 'Regulerer nervesystemet og trøster' },
                { name: 'Udfordring', desc: 'Styrker selvværd og mestring' }
              ].map((dim, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="p-6 rounded-2xl bg-[#4e6354] text-white shadow-md hover:shadow-lg hover:bg-[#3f5245] hover:scale-[1.02] transition-all duration-300 flex flex-col justify-between h-full group border border-[#4e6354]/10"
                >
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white">{dim.name}</h3>
                    <p className="text-stone-100 text-sm font-light leading-relaxed">{dim.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* The 3 Steps Process Flow with Image */}
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                
                {/* Left Column: Vertical Timeline */}
                <div className="lg:col-span-7">
                  <div className="mb-10">
                    <h3 className="text-2xl md:text-3xl font-light text-stone-900 uppercase tracking-tight mb-4">
                      Et typisk forløb i 3 trin
                    </h3>
                    <p className="text-stone-600 font-light leading-relaxed">
                      Forløbet er struktureret, så I hele tiden føler jer holdt i hånden og klædt på til at fortsætte de gode takter derhjemme.
                    </p>
                  </div>

                  <div className="relative pl-4 sm:pl-6 border-l-2 border-stone-200/60 space-y-12 ml-4">
                    {/* Step 1 */}
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="relative pl-8 sm:pl-10"
                    >
                      {/* Timeline Dot */}
                      <div className="absolute -left-[41px] top-1.5 w-8 h-8 rounded-full bg-[#4e6354] text-white flex items-center justify-center font-semibold text-sm shadow-md border-4 border-white">
                        1
                      </div>
                      
                      <div className="bg-white p-6 rounded-2xl border border-stone-200/40 shadow-sm hover:shadow-md transition-all duration-300">
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#4e6354] bg-[#4e6354]/10 px-2.5 py-1 rounded-full mb-3 inline-block">
                          Fokus: Samspil & Styrker
                        </span>
                        <h4 className="text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2">
                          <Users className="w-5 h-5 text-[#4e6354]" />
                          MIM-analyse
                        </h4>
                        <p className="text-stone-600 text-sm leading-relaxed font-light">
                          Vi starter med en struktureret samspilsobservation (Marschak Interaction Method), hvor jeg guider jer igennem nogle simple opgaver. Her ser vi på jeres unikke samspil og styrker.
                        </p>
                      </div>
                    </motion.div>

                    {/* Step 2 */}
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1, duration: 0.5 }}
                      className="relative pl-8 sm:pl-10"
                    >
                      {/* Timeline Dot */}
                      <div className="absolute -left-[41px] top-1.5 w-8 h-8 rounded-full bg-[#4e6354] text-white flex items-center justify-center font-semibold text-sm shadow-md border-4 border-white">
                        2
                      </div>
                      
                      <div className="bg-white p-6 rounded-2xl border border-stone-200/40 shadow-sm hover:shadow-md transition-all duration-300">
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#4e6354] bg-[#4e6354]/10 px-2.5 py-1 rounded-full mb-3 inline-block">
                          Fokus: Leg, Kontakt & Glæde
                        </span>
                        <h4 className="text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2">
                          <Smile className="w-5 h-5 text-[#4e6354]" />
                          Legesessioner
                        </h4>
                        <p className="text-stone-600 text-sm leading-relaxed font-light">
                          Forældre og barn deltager sammen i sjove, tilrettelagte lege (fx puste til vatkugler, tegne på hinandens rygge eller tjekke sår med plaster). Jeg guider dig undervejs, så du bliver mester i at støtte dit barn.
                        </p>
                      </div>
                    </motion.div>

                    {/* Step 3 */}
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                      className="relative pl-8 sm:pl-10"
                    >
                      {/* Timeline Dot */}
                      <div className="absolute -left-[41px] top-1.5 w-8 h-8 rounded-full bg-[#4e6354] text-white flex items-center justify-center font-semibold text-sm shadow-md border-4 border-white">
                        3
                      </div>
                      
                      <div className="bg-white p-6 rounded-2xl border border-stone-200/40 shadow-sm hover:shadow-md transition-all duration-300">
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#4e6354] bg-[#4e6354]/10 px-2.5 py-1 rounded-full mb-3 inline-block">
                          Fokus: Integration i Hverdagen
                        </span>
                        <h4 className="text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2">
                          <MessageSquare className="w-5 h-5 text-[#4e6354]" />
                          Forældresamtaler
                        </h4>
                        <p className="text-stone-600 text-sm leading-relaxed font-light">
                          Vi mødes løbende uden barnet for at evaluere, tale om fremskridt og omsætte de legende redskaber til jeres hverdag derhjemme.
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Right Column: Beautiful Image with Floating Badge */}
                <div className="lg:col-span-5 relative mt-8 lg:mt-0">
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="relative"
                  >
                    {/* Green glow effect behind the image */}
                    <div className="absolute -inset-10 bg-[#4e6354]/30 rounded-full blur-3xl opacity-70 pointer-events-none z-0" />
                    
                    {/* Decorative background element */}
                    <div className="absolute -inset-4 bg-[#4e6354]/5 rounded-3xl rounded-tr-[120px] rounded-bl-[120px] transform -rotate-2 scale-105 pointer-events-none z-0" />
                    
                    {/* Image Container */}
                    <div className="aspect-[4/5] rounded-3xl rounded-tr-[100px] rounded-bl-[100px] overflow-hidden shadow-lg border border-stone-200/40 relative z-10">
                      <img 
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/theraplay-connection.png" 
                        alt="Mor og barn leger glad sammen" 
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    </div>

                    {/* Floating Badge */}
                    <motion.div 
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      className="absolute -bottom-6 -right-4 sm:-right-8 bg-white/95 backdrop-blur-md rounded-2xl p-5 border border-stone-200/50 shadow-xl max-w-[240px] z-20"
                    >
                      <div className="flex items-center gap-2 justify-center text-[#4e6354] mb-1.5">
                        <GraduationCap className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-stone-800">Uddannelse</span>
                      </div>
                      <span className="block text-xs text-stone-600 font-light text-center leading-relaxed">
                        Theraplay-Internationalt-certificeret
                      </span>
                    </motion.div>
                  </motion.div>
                </div>

              </div>
            </div>
          </div>

          {/* Min faglige baggrund section transition spacer */}
        </section>
      )}

      {/* Section 4: Pakke & Pris */}
      {priceData && (
        <section className="py-20 md:py-28 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-xs font-semibold tracking-widest text-[#4e6354] uppercase block mb-2">
                Forløb & Pris
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-stone-900 uppercase tracking-tight mt-4 mb-6">
                {priceData.title.da}
              </h2>
              <p className="text-stone-600 font-light text-lg leading-relaxed">
                Et gennemtænkt og trygt forløb, der er personligt tilpasset til at skabe mærkbar og varig forandring i jeres familie.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch max-w-5xl mx-auto">
              {/* Left: Package Details */}
              <div className="lg:col-span-7 bg-white p-8 md:p-10 rounded-3xl border border-stone-200/40 shadow-sm flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-stone-900 mb-6">Hvad indeholder forløbet?</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#4e6354]/10 text-[#4e6354] flex items-center justify-center shrink-0 mt-1">
                        <Check className="w-3 h-3" />
                      </div>
                      <div>
                        <span className="font-semibold text-stone-800 block">1 x Forsamtale med forældre</span>
                        <span className="text-stone-500 text-sm font-light">Uden barn – vi afstemmer forventninger og udfordringer.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#4e6354]/10 text-[#4e6354] flex items-center justify-center shrink-0 mt-1">
                        <Check className="w-3 h-3" />
                      </div>
                      <div>
                        <span className="font-semibold text-stone-800 block">1 x MIM-samspilsanalyse</span>
                        <span className="text-stone-500 text-sm font-light">Forælder + barn – vi observerer jeres unikke samspil.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#4e6354]/10 text-[#4e6354] flex items-center justify-center shrink-0 mt-1">
                        <Check className="w-3 h-3" />
                      </div>
                      <div>
                        <span className="font-semibold text-stone-800 block">Theraplay-legesessioner</span>
                        <span className="text-stone-500 text-sm font-light">Forælder + barn – sjove, tilrettelagte lege, der styrker båndet. Antallet af sessioner tilpasses det enkelte barns behov.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#4e6354]/10 text-[#4e6354] flex items-center justify-center shrink-0 mt-1">
                        <Check className="w-3 h-3" />
                      </div>
                      <div>
                        <span className="font-semibold text-stone-800 block">Opfølgende forældresamtaler</span>
                        <span className="text-stone-500 text-sm font-light">Uden barn – vi evaluerer løbende og omsætter redskaber til hverdagen.</span>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="mt-8 pt-6 border-t border-stone-100 flex items-center gap-4 text-stone-500 text-sm font-light">
                  <Clock className="w-5 h-5 text-[#4e6354]" />
                  <span>Varighed: Tilpasses individuelt efter barnets behov og forløbets omfang.</span>
                </div>
              </div>

              {/* Right: Pricing Card */}
              <div className="lg:col-span-5 bg-gradient-to-br from-[#4e6354] to-[#3d4e42] text-white p-8 md:p-10 rounded-3xl shadow-xl flex flex-col justify-between relative overflow-hidden">
                <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-white/5 rounded-full blur-2xl pointer-events-none" />
                
                <div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-white/75 bg-white/10 px-3 py-1 rounded-full inline-block mb-6">
                    Pris & Forløb
                  </span>
                  <div className="mb-6">
                    <span className="text-5xl font-light tracking-tight">1.200</span>
                    <span className="text-2xl font-light ml-2">kr.</span>
                    <span className="block text-white/80 text-sm font-light mt-2">
                      pr. session (ca. halvanden time)
                    </span>
                  </div>
                  
                  <div className="space-y-4 text-sm text-white/90 font-light border-t border-white/10 pt-6">
                    <p className="flex items-start gap-2.5">
                      <Clock className="w-4 h-4 text-white/80 shrink-0 mt-0.5" />
                      <span><strong>Varighed:</strong> Ca. halvanden time pr. session inkl. samtaler og forberedelse.</span>
                    </p>
                    <p className="flex items-start gap-2.5">
                      <Users className="w-4 h-4 text-white/80 shrink-0 mt-0.5" />
                      <span><strong>Individuelt tilpasset:</strong> Det varierer efter barnet, hvor mange sessioner der er brug for.</span>
                    </p>
                    <p className="flex items-start gap-2.5">
                      <ShieldCheck className="w-4 h-4 text-white/80 shrink-0 mt-0.5" />
                      <span><strong>Eksempel:</strong> Et typisk forløb på f.eks. 10 sessioner koster samlet ca. 12.000 kr.</span>
                    </p>
                  </div>
                </div>

                <div className="mt-8">
                  <Link
                    to="/kontakt?subject=Theraplay"
                    className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-stone-100 text-[#4e6354] px-6 py-4 rounded-full font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    Spørg ind til forløb
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <p className="text-center text-xs text-white/90 mt-3 font-medium">
                    Kørsel afregnes udover prisen ved Theraplay forløb.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

    </div>
  );
}
